<script setup lang="ts">
import { computed, shallowRef, watchEffect } from 'vue'
import VIcon from '../VIcon/VIcon.vue'
import type { ILinkProps } from './composables/useLinkConfig'
import useLinkConfig from './composables/useLinkConfig'
import { defineNuxtLink } from '#app'
import { ICON_SIZES, SIZES, type TIconSizes } from '@/ui/ts/types/Sizes'
import { isDark } from '@/lib/utils'

const props = withDefaults(defineProps<ILinkProps>(), {
    size: SIZES.SMALL,
})

const linkConfig = shallowRef<Record<string, string>>({} as Record<string, string>)

const iconSize = computed<TIconSizes>(() => {
    if (props.size === SIZES.LARGE) return ICON_SIZES.MEDIUM
    if (props.size === SIZES.MEDIUM) return ICON_SIZES.SMALL

    return ICON_SIZES[props.size]
})

const is = computed(() => (!props.to && !props.href ? 'button' : defineNuxtLink({})))

const attributes = computed(() => {
    const baseAttributes = {
        id: props.id,
        class: [linkConfig.value.rootClasses, isDark(props.dark)],
        disabled: props.disabled,
        'aria-disabled': props.disabled,
        'data-test': `link-${props.dataTest}`,
        'data-gpp-tools-ui': true,
    }

    const linkAttributes = {
        ...baseAttributes,
        to: props.to || props.href,
        target: props.target,
    }

    const buttonAttributes = {
        ...baseAttributes,
    }

    return is.value === 'button' ? buttonAttributes : linkAttributes
})

watchEffect(() => {
    linkConfig.value = useLinkConfig(props)
})
</script>

<template>
    <component
        :is="is"
        v-bind="attributes"
    >
        <VIcon
            v-if="props.leadingIcon"
            :name="props.leadingIcon"
            class="block"
            :size="iconSize"
            :dark="props.dark"
            :class="props.leadingIconClasses"
        />
        <span class="w-fit">
            <slot />
        </span>
        <VIcon
            v-if="props.trailingIcon"
            :size="iconSize"
            :name="props.trailingIcon"
            :dark="props.dark"
            :class="props.trailingIconClasses"
        />
    </component>
</template>
